import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  Tooltip,
  useColorModeValue,
  VStack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Select,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverHeader,
  PopoverBody,
} from "@chakra-ui/react";
import React, { useMemo, useState } from "react";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import { useNavigate } from "react-router-dom";
import {
  FaRupeeSign,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaClock,
} from "react-icons/fa";
import {
  SearchIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InfoIcon,
} from "@chakra-ui/icons";
import { motion } from "framer-motion";

// Custom components
import { formattedIstDate } from "utils/utils";

// Motion components
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionTr = motion(Tr);

// Styled components
const GlassMorphismCard = ({ children, ...rest }) => {
  const bgColor = useColorModeValue(
    "rgba(255, 255, 255, 0.8)",
    "rgba(26, 32, 44, 0.8)"
  );
  const borderColor = useColorModeValue(
    "rgba(255, 255, 255, 0.5)",
    "rgba(255, 255, 255, 0.1)"
  );

  return (
    <Box
      bg={bgColor}
      backdropFilter="blur(10px)"
      borderRadius="2xl"
      boxShadow="xl"
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      transition="all 0.3s ease"
      _hover={{ boxShadow: "2xl" }}
      {...rest}
    >
      {children}
    </Box>
  );
};

const PaginationButton = ({ children, isActive, isDisabled, onClick }) => {
  return (
    <Button
      size="sm"
      variant={isActive ? "solid" : "outline"}
      colorScheme={isActive ? "brand" : "gray"}
      isDisabled={isDisabled}
      onClick={onClick}
      minW="40px"
      h="40px"
      borderRadius="md"
      _hover={!isDisabled && { transform: "translateY(-2px)", shadow: "md" }}
      transition="all 0.2s"
    >
      {children}
    </Button>
  );
};

const StatusBadge = ({ status }) => {
  let color, label;

  switch (status?.toLowerCase()) {
    case "completed":
      color = "green";
      label = "Completed";
      break;
    case "in_progress":
      color = "blue";
      label = "In Progress";
      break;
    case "failed":
      color = "red";
      label = "Failed";
      break;
    case "pending":
      color = "yellow";
      label = "Pending";
      break;
    default:
      color = "gray";
      label = status || "Unknown";
  }

  return (
    <Badge
      display="flex"
      alignItems="center"
      px={3}
      py={1}
      borderRadius="full"
      colorScheme={color}
      variant="subtle"
    >
      <Text fontSize="xs" fontWeight="medium">
        {label}
      </Text>
    </Badge>
  );
};

const ChargingSessionsColumnsTable = (props) => {
  const navigate = useNavigate();
  const { columnsData, tableData } = props;
  const columns = useMemo(() => columnsData, [columnsData]);
  const data = useMemo(() => tableData, [tableData]);
  const [searchQuery, setSearchQuery] = useState("");

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: 10,
      },
      globalFilter: (rows, ids, query) => {
        if (!query || query.trim() === "") {
          return rows;
        }

        const lowercasedQuery = query.toLowerCase().trim();

        return rows.filter((row) => {
          // Convert all values to strings and provide fallbacks for null/undefined values
          const sessionId = String(row.original.Id || "");
          const source = String(row.original.RequestSource || "");
          const status = String(row.original.Status || "");

          return (
            sessionId.toLowerCase().includes(lowercasedQuery) ||
            source.toLowerCase().includes(lowercasedQuery) ||
            status.toLowerCase().includes(lowercasedQuery)
          );
        });
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state: { pageIndex, pageSize },
    setGlobalFilter,
    gotoPage,
    setPageSize,
  } = tableInstance;

  const { canPreviousPage, canNextPage, pageCount, nextPage, previousPage } =
    tableInstance;

  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const themeColor = "#23A979";
  const theadBgColor = useColorModeValue("gray.50", "gray.800");

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setGlobalFilter(value);
  };

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      w="100%"
    >
      <GlassMorphismCard p={0}>
        {/* Search Section */}
        <Box p={6} borderBottom="1px solid" borderColor={borderColor}>
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            gap={4}
          >
            <VStack
              align="start"
              spacing={2}
              width={{ base: "100%", md: "auto" }}
            >
              <Text fontSize="xs" fontWeight="medium" color="gray.500">
                Search Sessions
              </Text>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="Search by ID, source, status..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  borderRadius="md"
                  width="350px"
                  _focus={{
                    borderColor: themeColor,
                    boxShadow: `0 0 0 1px ${themeColor}`,
                  }}
                />
              </InputGroup>
              <Text fontSize="xs" color="gray.500" mt={1} ml={1}>
                <Icon as={InfoIcon} boxSize={3} mr={1} />
                Type any keyword to search across all fields
              </Text>
            </VStack>

            <Flex align="center" justify="flex-end">
              <Text fontSize="xl" fontWeight="bold" color={textColor}>
                Charging Sessions
              </Text>
              <Text ml={2} color="gray.500" fontSize="sm">
                {data.length} total
              </Text>
            </Flex>
          </Flex>
        </Box>

        <TableContainer>
          <Table {...getTableProps()} variant="simple" colorScheme="gray">
            <Thead bg={theadBgColor}>
              {headerGroups.map((headerGroup, index) => (
                <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                  {headerGroup.headers.map((column, index) => (
                    <Th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      key={index}
                      py={4}
                      px={6}
                      borderColor={borderColor}
                      textTransform="uppercase"
                      fontSize="xs"
                      fontWeight="bold"
                      letterSpacing="wider"
                      color="gray.500"
                      textAlign="center"
                      cursor="pointer"
                      _hover={{ color: themeColor }}
                      transition="all 0.2s"
                    >
                      <Flex justify="center" align="center" gap={1}>
                        {column.render("Header")}
                        <Box>
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <Icon as={FaSortDown} />
                            ) : (
                              <Icon as={FaSortUp} />
                            )
                          ) : (
                            <Icon as={FaSort} opacity={0.3} />
                          )}
                        </Box>
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody {...getTableBodyProps()}>
              {page.map((row, index) => {
                prepareRow(row);
                return (
                  <MotionTr
                    {...row.getRowProps()}
                    key={index}
                    whileHover={{ backgroundColor: hoverBg }}
                    transition={{ duration: 0.2 }}
                    _hover={{ bg: hoverBg }}
                    cursor="pointer"
                  >
                    {row.cells.map((cell, index) => {
                      let data = "";
                      if (cell.column.Header === "S.No") {
                        data = (
                          <Flex align="center" justify="center">
                            <Text
                              color={textColor}
                              fontSize="sm"
                              fontWeight="600"
                            >
                              {Number(cell.row.id) + 1}
                            </Text>
                          </Flex>
                        );
                      } else if (cell.column.Header === "Charger ID") {
                        data = (
                          <Flex align="center" justify="center">
                            <Badge
                              colorScheme="brand"
                              variant="subtle"
                              borderRadius="md"
                              px={2}
                            >
                              {cell.row.original?.EvseId || "N/A"}
                            </Badge>
                          </Flex>
                        );
                      } else if (cell.column.Header === "Origin") {
                        data = (
                          <Flex align="center" justify="center">
                            <Badge
                              colorScheme="purple"
                              variant="subtle"
                              borderRadius="md"
                              px={2}
                            >
                              {cell.row.original?.RequestSource
                                ? cell.row.original?.RequestSource
                                : "NA"}
                            </Badge>
                          </Flex>
                        );
                      } else if (cell.column.Header === "Source") {
                        let textToRender = "";
                        const source = cell.row.original?.RequestSource
                          ? cell.row.original?.RequestSource
                          : "NA";
                        if (source === "rfid_authorized") {
                          textToRender = cell.row.original?.OcppIdTag
                            ? cell.row.original?.OcppIdTag
                            : "NA";
                        } else if (source === "CMS-UI") {
                          textToRender = cell.row.original?.SourceIdentifier
                            ? cell.row.original?.SourceIdentifier
                            : "NA";
                        } else if (source === "OCPI") {
                          textToRender = cell.row.original?.TokenPartyId
                            ? cell.row.original?.TokenPartyId
                            : "NA";
                        } else if (source === "PARTNER") {
                          textToRender = cell.row.original?.SourceIdentifier
                            ? cell.row.original?.SourceIdentifier
                            : "NA";
                        }
                        data = (
                          <Flex align="center" justify="center">
                            <Badge
                              colorScheme="blue"
                              variant="subtle"
                              borderRadius="md"
                              px={2}
                            >
                              {textToRender}
                            </Badge>
                          </Flex>
                        );
                      } else if (cell.column.Header === "Session Id") {
                        data = (
                          <Flex align="center" justify="center">
                            <Text
                              color={textColor}
                              fontSize="sm"
                              fontWeight="600"
                            >
                              {cell.row.original.Id}
                            </Text>
                          </Flex>
                        );
                      } else if (cell.column.Header === "Status") {
                        data = (
                          <Flex align="center" justify="center">
                            <StatusBadge
                              status={cell.row.original.Status || "Unknown"}
                            />
                          </Flex>
                        );
                      } else if (cell.column.Header === "Start Time") {
                        const startTime = cell.row.original.StartTime;
                        data = (
                          <Tooltip
                            label={formattedIstDate(startTime)}
                            placement="top"
                            hasArrow
                          >
                            <Flex align="center" justify="center">
                              <Icon as={FaClock} mr={1} color="gray.500" />
                              <Text color={textColor} fontSize="sm">
                                {formattedIstDate(startTime)}
                              </Text>
                            </Flex>
                          </Tooltip>
                        );
                      } else if (cell.column.Header === "End Time") {
                        const endTime = cell.row.original.EndTime;
                        data = (
                          <Tooltip
                            label={formattedIstDate(endTime)}
                            placement="top"
                            hasArrow
                          >
                            <Flex align="center" justify="center">
                              <Icon as={FaClock} mr={1} color="gray.500" />
                              <Text color={textColor} fontSize="sm">
                                {formattedIstDate(endTime)}
                              </Text>
                            </Flex>
                          </Tooltip>
                        );
                      } else if (
                        cell.column.Header === "No of Units Dispatched"
                      ) {
                        data = (
                          <Flex align="center" justify="center">
                            <Badge
                              colorScheme="blue"
                              variant="subtle"
                              borderRadius="full"
                              px={3}
                              py={1}
                            >
                              {cell.row.original.UnitsConsumed.toFixed(3)} kWh
                            </Badge>
                          </Flex>
                        );
                      } else if (cell.column.Header === "Total Amount") {
                        data = (
                          <Flex
                            color={textColor}
                            fontSize="md"
                            fontWeight="700"
                            alignItems="center"
                            justify="center"
                          >
                            <Icon
                              as={FaRupeeSign}
                              w={3}
                              h={4}
                              mr={2}
                              color={themeColor}
                            />
                            <Text>
                              {cell.row.original?.TotalCostWithTax?.toFixed(2)}
                            </Text>
                          </Flex>
                        );
                      }
                      return (
                        <Td
                          {...cell.getCellProps()}
                          key={index}
                          py={4}
                          px={6}
                          borderColor={borderColor}
                          fontSize="sm"
                        >
                          {data}
                        </Td>
                      );
                    })}
                  </MotionTr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Flex
          p={4}
          justify="space-between"
          align="center"
          borderTop="1px solid"
          borderColor={borderColor}
          flexWrap="wrap"
          gap={4}
        >
          <HStack>
            <Select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              size="sm"
              borderRadius="md"
              w="120px"
            >
              {[5, 10, 20, 30, 40, 50].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </Select>
            <Text fontSize="sm" color="gray.500">
              Showing {page.length} of {data.length} results
            </Text>
          </HStack>

          <HStack spacing={2}>
            <PaginationButton
              isDisabled={!canPreviousPage}
              onClick={() => gotoPage(0)}
            >
              <ChevronLeftIcon boxSize={4} mr={1} />
              First
            </PaginationButton>

            <PaginationButton
              isDisabled={!canPreviousPage}
              onClick={() => previousPage()}
            >
              <ChevronLeftIcon boxSize={4} />
            </PaginationButton>

            {pageCount <= 5 ? (
              // If we have 5 or fewer pages, show all page buttons
              [...Array(pageCount)].map((_, i) => (
                <PaginationButton
                  key={i}
                  isActive={pageIndex === i}
                  onClick={() => gotoPage(i)}
                >
                  {i + 1}
                </PaginationButton>
              ))
            ) : (
              // If we have more than 5 pages, show a subset
              <>
                {pageIndex > 1 && (
                  <PaginationButton onClick={() => gotoPage(0)}>
                    1
                  </PaginationButton>
                )}

                {pageIndex > 2 && <Text>...</Text>}

                {pageIndex > 0 && (
                  <PaginationButton onClick={() => gotoPage(pageIndex - 1)}>
                    {pageIndex}
                  </PaginationButton>
                )}

                <PaginationButton isActive onClick={() => gotoPage(pageIndex)}>
                  {pageIndex + 1}
                </PaginationButton>

                {pageIndex < pageCount - 1 && (
                  <PaginationButton onClick={() => gotoPage(pageIndex + 1)}>
                    {pageIndex + 2}
                  </PaginationButton>
                )}

                {pageIndex < pageCount - 3 && <Text>...</Text>}

                {pageIndex < pageCount - 2 && (
                  <PaginationButton onClick={() => gotoPage(pageCount - 1)}>
                    {pageCount}
                  </PaginationButton>
                )}
              </>
            )}

            <PaginationButton
              isDisabled={!canNextPage}
              onClick={() => nextPage()}
            >
              <ChevronRightIcon boxSize={4} />
            </PaginationButton>

            <PaginationButton
              isDisabled={!canNextPage}
              onClick={() => gotoPage(pageCount - 1)}
            >
              Last
              <ChevronRightIcon boxSize={4} ml={1} />
            </PaginationButton>
          </HStack>
        </Flex>
      </GlassMorphismCard>
    </MotionBox>
  );
};
export default ChargingSessionsColumnsTable;
